import { useState, useCallback, useEffect, useRef } from 'react';
import { Star } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import type { PlatformStatus, IncidentStatusChange, DefenderClassification, DefenderDetermination } from '../../data/types';
import { getStatusHistory, updateIncidentStatus, submitIncidentFeedback, flagIncidentAsNoise } from '../../services/incidents';
import { getClassificationOptions, getDeterminationOptions } from '../../services/defender';

/**
 * What the analyst needs in front of them at the moment of closing. Mirrors
 * the facts the analyst-api closure guard checks (lib/closureGuard.ts), so the
 * modal can warn before the server refuses.
 */
export interface CloseContext {
  severity?: string | null;
  riskScore?: number | null;
  escalate?: boolean | null;
  recommendedAction?: string | null;
  alertCount: number;
  truePositiveAlertCount: number;
}

/**
 * Guidance the analyst-api sends when it holds a closure back
 * (`category: 'closure_guard'`). Mirrors ClosureExplanation in
 * func-axn-analyst-api/src/lib/closureGuard.ts.
 */
interface ClosureExplanation {
  title: string;
  found: string[];
  why: string;
  nextSteps: string[];
  resolvedBy: 'reason' | 'senior-analyst';
}

function readClosureExplanation(body: unknown): ClosureExplanation | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as { category?: unknown; explanation?: unknown };
  if (b.category !== 'closure_guard' || !b.explanation || typeof b.explanation !== 'object') return null;
  const e = b.explanation as Partial<ClosureExplanation>;
  if (typeof e.title !== 'string' || !Array.isArray(e.found) || !Array.isArray(e.nextSteps)) return null;
  return {
    title: e.title,
    found: e.found.filter((x): x is string => typeof x === 'string'),
    why: typeof e.why === 'string' ? e.why : '',
    nextSteps: e.nextSteps.filter((x): x is string => typeof x === 'string'),
    resolvedBy: e.resolvedBy === 'senior-analyst' ? 'senior-analyst' : 'reason',
  };
}

const BENIGN_CLASSIFICATIONS: DefenderClassification[] = ['falsePositive', 'informationalExpectedActivity'];
const MIN_REASON_LENGTH = 20;

interface StatusManagerProps {
  tenantId: string;
  incidentId: string;
  currentStatus: PlatformStatus;
  onStatusChange?: (newStatus: PlatformStatus) => void;
  closeContext?: CloseContext;
  variant?: 'inline' | 'dropdown';
  className?: string;
}

const STATUS_CONFIG: Record<PlatformStatus, { label: string; color: string; bgColor: string }> = {
  new: { 
    label: 'New', 
    color: 'text-amber-700 dark:text-amber-400',
    bgColor: 'bg-amber-100 dark:bg-amber-900/30'
  },
  acknowledged: { 
    label: 'Acknowledged', 
    color: 'text-blue-700 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  investigating: { 
    label: 'Investigating', 
    color: 'text-purple-700 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30'
  },
  closed: { 
    label: 'Closed', 
    color: 'text-green-700 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  reopened: { 
    label: 'Reopened', 
    color: 'text-red-700 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  },
};

const STATUS_ORDER: PlatformStatus[] = ['new', 'acknowledged', 'investigating', 'closed', 'reopened'];

export function StatusManager({
  tenantId,
  incidentId,
  currentStatus,
  onStatusChange,
  closeContext,
  variant = 'dropdown',
  className,
}: StatusManagerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<PlatformStatus | null>(null);
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [defenderSyncResult, setDefenderSyncResult] = useState<'success' | 'error' | 'not_attempted' | null>(null);
  const [feedbackWarning, setFeedbackWarning] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);
  // Set when the API holds a closure back and explains why — shown as guidance, not as an error.
  const [closureHold, setClosureHold] = useState<ClosureExplanation | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);

  // The guidance and any error render at the top of a scrolling dialog, while
  // the button that triggers them is at the bottom. Bring them into view.
  useEffect(() => {
    if (closureHold || updateError) {
      feedbackRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [closureHold, updateError]);
  
  // Defender classification fields (only needed when closing)
  // No pre-selection: a close used to open on True Positive / Malware, so an
  // analyst who clicked through recorded a confirmed attack in Defender by default.
  const [classification, setClassification] = useState<DefenderClassification | ''>('');
  const [determination, setDetermination] = useState<DefenderDetermination | ''>('');

  // AI feedback fields (only shown when closing)
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackHover, setFeedbackHover] = useState(0);
  const [feedbackComments, setFeedbackComments] = useState('');
  const [feedbackPlaybook, setFeedbackPlaybook] = useState('');
  const [feedbackCategory, setFeedbackCategory] = useState('overall');
  const feedbackIdempotencyKey = useRef(crypto.randomUUID());

  // Noise flag (only shown for FP/Informational classifications)
  const [flagNoise, setFlagNoise] = useState(false);
  const [noiseNote, setNoiseNote] = useState('');

  const config = STATUS_CONFIG[currentStatus];
  const classificationOptions = getClassificationOptions();
  const determinationOptions = classification ? getDeterminationOptions(classification) : [];

  // Mirrors the analyst-api closure guard: any closure above medium severity
  // needs a written comment; so does a benign closure over a triage escalation
  // or over alerts already marked true positive.
  const closing = pendingStatus === 'closed';
  const closingBenign = closing && classification !== '' && BENIGN_CLASSIFICATIONS.includes(classification);
  const classificationMissing = closing && (classification === '' || determination === '');
  const aiEscalated = closeContext?.escalate === true || (closeContext?.riskScore ?? 0) >= 7;
  const highSeverity = ['high', 'critical'].includes((closeContext?.severity ?? '').toLowerCase());
  const truePositiveAlerts = closeContext?.truePositiveAlertCount ?? 0;
  const reasonRequired = closing && (highSeverity || (closingBenign && (aiEscalated || truePositiveAlerts > 0)));
  const reasonMissing = reasonRequired && reason.trim().length < MIN_REASON_LENGTH;
  // Why the comment is being asked for, in the order an analyst would care about.
  const reasonWhy = closingBenign && truePositiveAlerts > 0
    ? 'alerts on this incident are marked true positive in Defender'
    : highSeverity
      ? 'this incident is high severity'
      : 'triage escalated this incident';
  const charsNeeded = Math.max(0, MIN_REASON_LENGTH - reason.trim().length);

  // Take the route the guidance recommends: keep the incident open and hand it on.
  const switchToInvestigating = () => {
    setClosureHold(null);
    setUpdateError(null);
    setPendingStatus('investigating');
    if (!reason.trim()) setReason('Needs senior review before closing: ');
    reasonRef.current?.focus();
  };

  const handleStatusSelect = (status: PlatformStatus) => {
    if (status === currentStatus) {
      setIsOpen(false);
      return;
    }
    setPendingStatus(status);
    setShowConfirm(true);
    setIsOpen(false);
    setDefenderSyncResult(null);
    setUpdateError(null);
    setClosureHold(null);
  };

  const handleConfirm = async () => {
    if (!pendingStatus) return;

    setUpdating(true);
    setUpdateError(null);
    setClosureHold(null);
    // analyst-api requires classification + determination when closing
    // (validated server-side since 2026-05-14, returns 400 otherwise).
    const response = await updateIncidentStatus(
      tenantId,
      incidentId,
      pendingStatus,
      reason.trim() || undefined,
      pendingStatus === 'closed' && classification ? classification : undefined,
      pendingStatus === 'closed' && determination ? determination : undefined,
    );

    if (response.success) {
      onStatusChange?.(pendingStatus);
      
      // Await feedback so the modal never claims it was captured before the API
      // has durably accepted it. Closure remains successful if feedback fails.
      if (pendingStatus === 'closed' && feedbackRating > 0) {
        const feedbackResponse = await submitIncidentFeedback(tenantId, incidentId, {
          feedbackType: feedbackCategory,
          rating: feedbackRating,
          comments: feedbackComments.trim() || undefined,
          expectedValue: feedbackPlaybook.trim() || undefined,
          source: 'closure',
          idempotencyKey: feedbackIdempotencyKey.current,
        });
        if (!feedbackResponse.success) {
          setFeedbackWarning('Incident closed, but AI feedback was not saved. Please retry from the incident sections.');
        }
      }

      // Fire-and-forget noise flag — creates a pending recommendation for operator review
      if (pendingStatus === 'closed' && flagNoise) {
        flagIncidentAsNoise(tenantId, incidentId, {
          classification: classification as DefenderClassification,
          note: noiseNote.trim() || undefined,
        }).catch(err => console.error('Noise flag submission failed:', err));
      }
      
      // updateIncidentStatus performs the Defender write-back server-side.
      if (pendingStatus === 'closed') {
        setDefenderSyncResult(
          !response.defenderSync?.attempted
            ? 'not_attempted'
            : response.defenderSync.succeeded ? 'success' : 'error',
        );
      }
    }

    if (!response.success) {
      // A closure the guard held back comes with an explanation — show that as
      // guidance. Anything else is a real failure and is shown as an error.
      const explanation = readClosureExplanation(response.errorBody);
      if (explanation) {
        setClosureHold(explanation);
      } else {
        setUpdateError(response.error || 'The status could not be updated.');
      }
      setUpdating(false);
      return;
    }

    setUpdating(false);
    // Keep modal open briefly to show Defender sync result for closed status
    if (pendingStatus !== 'closed') {
      setShowConfirm(false);
      setPendingStatus(null);
      setReason('');
    }
  };
  
  const handleCloseModal = () => {
    setShowConfirm(false);
    setPendingStatus(null);
    setReason('');
    setDefenderSyncResult(null);
    setUpdateError(null);
    setClosureHold(null);
    setFeedbackWarning(null);
    setFeedbackRating(0);
    setFeedbackHover(0);
    setFeedbackComments('');
    setFeedbackPlaybook('');
    setFeedbackCategory('overall');
    feedbackIdempotencyKey.current = crypto.randomUUID();
    setFlagNoise(false);
    setNoiseNote('');
  };

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {STATUS_ORDER.map(status => {
          const cfg = STATUS_CONFIG[status];
          const isActive = status === currentStatus;
          return (
            <button
              key={status}
              onClick={() => handleStatusSelect(status)}
              className={cn(
                'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                isActive
                  ? cn(cfg.bgColor, cfg.color)
                  : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary'
              )}
            >
              {cfg.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <>
      <div className={cn('relative', className)}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium transition-colors',
            'min-h-11 md:min-h-0',
            config.bgColor,
            config.color,
            'hover:opacity-80'
          )}
        >
          <StatusDot status={currentStatus} />
          <span>{config.label}</span>
          <ChevronDownIcon className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute top-full left-0 mt-1 z-50 w-48 bg-bg-secondary rounded-lg shadow-lg border border-border-default py-1 animate-fade-in">
              {STATUS_ORDER.map(status => {
                const cfg = STATUS_CONFIG[status];
                const isActive = status === currentStatus;
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusSelect(status)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                      'min-h-11 md:min-h-0',
                      isActive
                        ? 'bg-bg-tertiary'
                        : 'hover:bg-bg-tertiary/50',
                      cfg.color
                    )}
                  >
                    <StatusDot status={status} />
                    <span>{cfg.label}</span>
                    {isActive && <CheckIcon className="w-4 h-4 ml-auto" />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirm}
        onClose={handleCloseModal}
        title="Change Incident Status"
        description={`Update status from ${STATUS_CONFIG[currentStatus].label} to ${pendingStatus ? STATUS_CONFIG[pendingStatus].label : ''}`}
        size={pendingStatus === 'closed' ? 'md' : 'sm'}
        footer={
          defenderSyncResult ? (
            <div className="flex justify-end">
              <Button onClick={handleCloseModal}>
                Close
              </Button>
            </div>
          ) : (
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={handleCloseModal}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={updating || reasonMissing || classificationMissing}>
                {updating ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          )
        }
      >
        <div className="space-y-4">
          {/* Visual status change */}
          <div className="flex items-center justify-center gap-4">
            <StatusBadge status={currentStatus} />
            <ArrowRightIcon className="w-5 h-5 text-text-muted" />
            {pendingStatus && <StatusBadge status={pendingStatus} />}
          </div>

          {/* Defender sync result message */}
          {defenderSyncResult && (
            <div className={cn(
              'p-3 rounded-lg flex items-center gap-2',
              defenderSyncResult === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : defenderSyncResult === 'error'
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                  : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
            )}>
              {defenderSyncResult === 'success' ? (
                <>
                  <CheckIcon className="w-5 h-5" />
                  <span>Status updated and synced to Microsoft Defender</span>
                </>
              ) : defenderSyncResult === 'error' ? (
                <>
                  <WarningIcon className="w-5 h-5" />
                  <span>Status updated locally, but Defender sync failed. You may need to update Defender manually.</span>
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  <span>Status updated. No linked Defender incident required a write-back.</span>
                </>
              )}
            </div>
          )}

          {feedbackWarning && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400" role="alert">
              {feedbackWarning}
            </div>
          )}

          <div ref={feedbackRef} className="scroll-mt-4" />
          {updateError && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300" role="alert">
              {updateError}
            </div>
          )}

          {closureHold && (
            <div className="p-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 space-y-3 text-sm" role="status" aria-live="polite">
              <div className="flex items-start gap-2">
                <WarningIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="font-semibold text-amber-900 dark:text-amber-100">{closureHold.title}</div>
              </div>
              {closureHold.found.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1">What we found</div>
                  <ul className="list-disc pl-5 space-y-0.5 text-amber-900 dark:text-amber-100">
                    {closureHold.found.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                </div>
              )}
              {closureHold.why && (
                <div>
                  <div className="text-xs font-medium text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1">Why it matters</div>
                  <p className="text-amber-900 dark:text-amber-100">{closureHold.why}</p>
                </div>
              )}
              {closureHold.nextSteps.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-amber-800 dark:text-amber-300 uppercase tracking-wide mb-1">What to do next</div>
                  <ol className="list-decimal pl-5 space-y-0.5 text-amber-900 dark:text-amber-100">
                    {closureHold.nextSteps.map((n) => <li key={n}>{n}</li>)}
                  </ol>
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <Button size="sm" onClick={switchToInvestigating}>Move to Investigating instead</Button>
                {closureHold.resolvedBy === 'reason' && (
                  <Button size="sm" variant="ghost" onClick={() => { setClosureHold(null); reasonRef.current?.focus(); }}>
                    Add a closing comment
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* What triage and Defender say, shown at the point of closing */}
          {pendingStatus === 'closed' && !defenderSyncResult && closeContext && (
            <div className="p-3 rounded-lg border border-border-default bg-bg-tertiary space-y-2 text-sm">
              <div className="text-xs font-medium text-text-secondary uppercase tracking-wide">Before you close</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-text-primary">
                {closeContext.severity && <span>Severity: <strong className="capitalize">{closeContext.severity}</strong></span>}
                {closeContext.riskScore !== null && closeContext.riskScore !== undefined && (
                  <span>Triage risk: <strong>{closeContext.riskScore}/10</strong>{aiEscalated ? ' — escalated' : ''}</span>
                )}
                <span>Alerts: <strong>{closeContext.alertCount}</strong></span>
              </div>
              {closeContext.recommendedAction && (
                <div className="text-text-secondary">Recommended action: {closeContext.recommendedAction}</div>
              )}
              {truePositiveAlerts > 0 && (
                <div className="flex items-start gap-2 p-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100">
                  <WarningIcon className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                  <span>
                    {truePositiveAlerts} of {closeContext.alertCount} alerts are marked <strong>true positive</strong> in Defender,
                    which means someone has already judged this activity to be real. If you agree, close it as True Positive.
                    If you think it is benign, move it to Investigating and ask a senior analyst to review it.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Syncing indicator */}
          {/* Defender classification fields when closing */}
          {pendingStatus === 'closed' && !defenderSyncResult && (
            <>
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-2">
                  <WarningIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    Closing this incident will automatically resolve it in Microsoft Defender with the classification below.
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Classification
                </label>
                <select
                  value={classification}
                  onChange={(e) => {
                    const newClassification = e.target.value as DefenderClassification | '';
                    setClassification(newClassification);
                    setDetermination('');
                  }}
                  className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue"
                >
                  <option value="">Choose how this incident is classified…</option>
                  {classificationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Determination
                </label>
                <select
                  value={determination}
                  disabled={classification === ''}
                  onChange={(e) => setDetermination(e.target.value as DefenderDetermination | '')}
                  className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:opacity-60"
                >
                  <option value="">{classification ? 'Choose a determination…' : 'Choose a classification first'}</option>
                  {determinationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Noise flag — only for FP/Informational classifications */}
          {pendingStatus === 'closed' && !defenderSyncResult
            && (classification === 'falsePositive' || classification === 'informationalExpectedActivity') && (
            <div className="p-3 rounded-lg bg-bg-tertiary/50 border border-border-default">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={flagNoise}
                  onChange={(e) => setFlagNoise(e.target.checked)}
                  className="w-4 h-4 rounded border-border-default text-brand-blue focus:ring-brand-blue"
                />
                <span className="text-sm font-medium text-text-secondary">
                  Flag as noise for this tenant
                </span>
              </label>
              <p className="mt-1 ml-6 text-xs text-text-secondary">
                Creates a recommendation for operators to add this as a known noise pattern.
              </p>
              {flagNoise && (
                <textarea
                  value={noiseNote}
                  onChange={(e) => setNoiseNote(e.target.value)}
                  placeholder="Why is this noise? e.g. Recurring operational alert from Sentinel NRT rule..."
                  rows={2}
                  className="mt-2 w-full px-3 py-2 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none text-sm"
                />
              )}
            </div>
          )}

          {/* AI Feedback — shown prominently before closing comment */}
          {pendingStatus === 'closed' && !defenderSyncResult && (
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-text-secondary">
                  Rate the AI Analysis
                </div>
                <span className="text-xs text-accent-blue">
                  Helps improve accuracy per tenant
                </span>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedbackRating(star)}
                        onMouseEnter={() => setFeedbackHover(star)}
                        onMouseLeave={() => setFeedbackHover(0)}
                        className="p-0.5 transition-transform hover:scale-110"
                      >
                        <Star
                          className={cn(
                            'w-7 h-7 transition-colors',
                            star <= (feedbackHover || feedbackRating)
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-text-muted'
                          )}
                        />
                      </button>
                    ))}
                    {feedbackRating > 0 && (
                      <span className="ml-2 text-sm font-medium text-text-secondary">{feedbackRating}/5</span>
                    )}
                  </div>
                </div>

                {feedbackRating > 0 && (
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">
                      Anything to improve? (optional)
                    </label>
                    <textarea
                      value={feedbackComments}
                      onChange={(e) => setFeedbackComments(e.target.value)}
                      placeholder="e.g. Risk score was too high, recommendations were too aggressive..."
                      rows={2}
                      className="w-full px-3 py-2 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none text-sm"
                    />
                  </div>
                )}

                {feedbackRating > 0 && feedbackRating <= 3 && (
                  <div>
                    <label className="block text-xs text-text-secondary mb-1.5">
                      What was the main issue?
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { value: 'risk', label: 'Risk Score' },
                        { value: 'actions', label: 'Actions' },
                        { value: 'summary', label: 'Summary' },
                        { value: 'mitre', label: 'MITRE' },
                        { value: 'overall', label: 'Other' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFeedbackCategory(opt.value)}
                          className={cn(
                            'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
                            feedbackCategory === opt.value
                              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
                              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-tertiary'
                          )}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {feedbackRating > 0 && feedbackRating <= 3 && (
                  <div>
                    <label className="block text-xs text-text-secondary mb-1">
                      Expected playbook (optional)
                    </label>
                    <input
                      type="text"
                      value={feedbackPlaybook}
                      onChange={(e) => setFeedbackPlaybook(e.target.value)}
                      placeholder="e.g. pb-email-threat"
                      className="w-full px-3 py-2 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reason (optional) */}
          {!defenderSyncResult && (
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                {pendingStatus === 'closed' ? 'Closing Comment' : 'Reason'} {reasonRequired ? '(required)' : '(optional)'}
              </label>
              <textarea
                ref={reasonRef}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  reasonRequired
                    ? (closingBenign
                        ? 'What did you check, and what did it show? (will be visible in Defender)'
                        : 'What was confirmed, and what was done about it? (will be visible in Defender)')
                    : pendingStatus === 'closed' ? 'Add a closing comment (will be visible in Defender)...' : 'Add a note about this status change...'
                }
                rows={reasonRequired ? 3 : 2}
                className="w-full px-3 py-2 bg-bg-tertiary border border-border-default rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue resize-none"
              />
              {reasonRequired && (
                <p className="mt-1 text-xs text-text-secondary">
                  A closing comment is needed because {reasonWhy}.{' '}
                  {closingBenign ? 'Say what you checked and what it showed' : 'Say what was confirmed and what was done about it'} —
                  it is the record the customer and reviewers will read.
                  {charsNeeded > 0 && <span className="text-amber-700 dark:text-amber-400"> {charsNeeded} more character{charsNeeded === 1 ? '' : 's'} needed.</span>}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}

// ============================================================================
// STATUS HISTORY TIMELINE
// ============================================================================

interface StatusHistoryProps {
  tenantId: string;
  incidentId: string;
  className?: string;
}

export function StatusHistory({ tenantId, incidentId, className }: StatusHistoryProps) {
  const [history, setHistory] = useState<IncidentStatusChange[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    const response = await getStatusHistory(tenantId, incidentId);
    if (response.success) {
      setHistory(response.data);
    }
    setLoading(false);
  }, [tenantId, incidentId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3 animate-pulse">
            <div className="w-3 h-3 rounded-full bg-bg-tertiary mt-1" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-32 bg-bg-tertiary rounded" />
              <div className="h-3 w-48 bg-bg-tertiary rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className={cn('text-sm text-text-secondary', className)}>
        No status history available.
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {history.map((change, index) => (
        <div key={change.historyId} className="flex gap-3">
          {/* Timeline dot and line */}
          <div className="flex flex-col items-center">
            <StatusDot status={change.newStatus} size="md" />
            {index < history.length - 1 && (
              <div className="w-px h-full bg-bg-tertiary mt-1" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 pb-4">
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                {STATUS_CONFIG[change.newStatus].label}
              </span>
              {change.previousStatus && (
                <span className="text-sm text-text-secondary">
                  from {STATUS_CONFIG[change.previousStatus].label}
                </span>
              )}
            </div>
            <div className="text-sm text-text-secondary">
              {change.changedByName || 'System'}{change.actor === 'autopilot' && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border bg-accent-blue/15 text-accent-blue border-accent-blue/40" title="Closed automatically by Autopilot under this customer's policy; the customer sees an ordinary closure">Autopilot</span>} • {formatDateTime(change.changedAt)}
            </div>
            {change.reason && (
              <p className="mt-1 text-sm text-text-secondary">
                {change.reason}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function StatusDot({ status, size = 'sm' }: { status: PlatformStatus; size?: 'sm' | 'md' }) {
  const colors: Record<PlatformStatus, string> = {
    new: 'bg-amber-500',
    acknowledged: 'bg-blue-500',
    investigating: 'bg-purple-500',
    closed: 'bg-green-500',
    reopened: 'bg-red-500',
  };

  return (
    <span className={cn(
      'rounded-full',
      colors[status],
      size === 'sm' ? 'w-2 h-2' : 'w-3 h-3'
    )} />
  );
}

function StatusBadge({ status }: { status: PlatformStatus }) {
  const config = STATUS_CONFIG[status];
  return (
    <span className={cn(
      'flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium',
      config.bgColor,
      config.color
    )}>
      <StatusDot status={status} />
      {config.label}
    </span>
  );
}

// ============================================================================
// ICONS
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}
